/**
 * Decode the most common HTML character entities back to plain text.
 *
 * The CMS API returns plain-text fields (title, summary, author) HTML-encoded
 * (e.g. `IP &amp; Copyright Takedown`). React renders strings literally, so
 * without decoding we'd paint the raw `&amp;` into the DOM. Use on plain-text
 * fields only — never on `content` bound to dangerouslySetInnerHTML.
 *
 * Ported from ff-ui (src/client/utils/htmlText.ts) so the admin preview decodes
 * identically to the public page.
 */

const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  hellip: '…', mdash: '—', ndash: '–', lsquo: '‘',
  rsquo: '’', ldquo: '“', rdquo: '”', laquo: '«',
  raquo: '»', copy: '©', reg: '®', trade: '™',
  middot: '·', bull: '•', deg: '°', para: '¶',
  sect: '§', euro: '€', pound: '£', yen: '¥', cent: '¢',
};

const ENTITY_RE = /&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi;
const MAX_CODE_POINT = 0x10ffff;

export function decodeHtmlEntities(input: string | null | undefined): string {
  if (!input) return '';
  if (typeof input !== 'string') return String(input);
  if (input.indexOf('&') === -1) return input;

  return input.replace(ENTITY_RE, (match, code: string) => {
    if (code[0] === '#') {
      const isHex = code[1] === 'x' || code[1] === 'X';
      const num = isHex ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      if (!Number.isFinite(num) || num <= 0 || num > MAX_CODE_POINT) return match;
      try {
        return String.fromCodePoint(num);
      } catch {
        return match;
      }
    }
    const named = NAMED_ENTITIES[code.toLowerCase()];
    return named ?? match;
  });
}

/**
 * Decode a nullable string array (e.g. CMS `authors`). Empty / nullish inputs
 * collapse to `null` so callers don't special-case empty arrays.
 */
export function decodeHtmlEntitiesArray(
  input: ReadonlyArray<string | null | undefined> | null | undefined,
): string[] | null {
  if (!input?.length) return null;
  const out = input.map((item) => decodeHtmlEntities(item)).filter((s): s is string => !!s);
  return out.length ? out : null;
}
